import { parse, format, AsYouType } from 'libphonenumber-js';
import { isValidNumber } from 'libphonenumber-js/custom';
import metadata from 'libphonenumber-js/metadata.full.json';
import { i18n } from 'src/features';
import formatDateTime from 'src/filters/formatDateTime';
import { mobileCheckRegExp1, mobileCheckRegExp2 } from './mobileCheckRegexps';

function writeLogs(data) {
  window.$store.dispatch('logs/setLogsAndUserInfo', data);
}

const dirname = __dirname;
const { isArray } = Array;
const isDevelop = process.env.NODE_ENV === 'development';
const nowTime = (date = new Date()) => formatDateTime(date, true);
const l = ((oldCons) => ({
  log: (text) => {
    oldCons.log(`[${nowTime()}]`, text);
  },
  info: (text) => {
    oldCons.info(`[${nowTime()}]`, text);
  },
  warn: (text, info) => {
    const warnObj = {
      type: 'warn',
      info,
      date: new Date(),
      text,
    };
    oldCons.warn(`[${nowTime()}]`, text);
    writeLogs(warnObj);
  },
  error: (text, info) => {
    const errorObj = {
      type: 'error',
      info,
      date: new Date(),
      text,
    };
    oldCons.error(`[${nowTime()}]`, text);
    writeLogs(errorObj);
  },
}))(window.console);

const isEmpty = (str) => (!str || str.length === 0 || /^\s*$/.test(str));

const formattedMessage = (msg, customerType, currentID, isCurrent = false) => {
  const insetTime = (msg.insertedAt.getTime ? msg.insertedAt : new Date(msg.insertedAt)).getTime();

  return {
    activityId: msg.activityId,
    attachmentInfo: msg.attachmentInfo,
    attachments: msg.attachments,
    auto: msg.auto,
    avatarUrl: msg.avatarUrl,
    copies: msg.copies,
    customerId: msg.customerId,
    customerType: isEmpty(msg.customerType) ? customerType : msg.customerType,
    date: new Date(insetTime),
    file: msg.file,
    forbiddenToSend: msg.forbiddenToSend,
    from: msg.from,
    id: parseInt(msg.id, 10) || undefined,
    insertedAt: new Date(insetTime),
    isRead: msg.isRead,
    message: msg.body,
    operatorId: msg.operatorId,
    progress: msg.progress,
    receiver: msg.receiver,
    sender: msg.sender,
    tag: msg.tag,
    temporaryId: msg.temporaryId,
    to: msg.to,
    unableToSend: msg.unableToSend,
    updatedAt: new Date(insetTime),
    username: msg.username,
    current: isCurrent,
    currentID,
    type: msg.type,
    isOperatorSending: msg.isOperatorSending || false,
  };
};

const generateUniqueId = (length) => {
  let text = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i += 1) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
};

const maskPhoneI8isValidNumber = (number) => isValidNumber(parse(number, 'RU'), metadata);

const maskPhoneI8Static = (number) => {
  let resPhone = format({ country: 'RU', phone: parse(number, 'RU').phone }, 'International');
  if (resPhone) {
    resPhone = resPhone.substring(0, 16);
  }
  return resPhone;
};

const maskPhoneEachSymbol = (numberString) => {
  const formatter = new AsYouType('RU');
  let resultNumber;
  const inputCheckPhone = numberString ? numberString.match(/[[^\d|+\-()]/gi) : '';
  const number = inputCheckPhone ? inputCheckPhone.join().replace(/[,]/g, '') : '';
  const resNum = number.split('');
  resNum.forEach((ch) => {
    resultNumber = formatter.input(ch);
  });
  if (resultNumber) {
    resultNumber = resultNumber.substring(0, 16);
  }
  try {
    if (maskPhoneI8isValidNumber(resultNumber)) {
      return maskPhoneI8Static(resultNumber);
    }
    return resultNumber;
  } catch (err) {
    return resultNumber;
  }
};

const validateFormatPhone = (numberString) => {
  try {
    if (maskPhoneI8isValidNumber(numberString)) {
      return maskPhoneI8Static(numberString);
    }
    return maskPhoneEachSymbol(numberString);
  } catch (err) {
    return maskPhoneEachSymbol(numberString);
  }
};

/**
 * Append array element index to new array
 * ex. [1, 2] will be [{index: 0}, {index: 1}]
 * ex. [{value: 2}] will be [{index: 0, value: 2}]
 * @param {Array} array Input array
 * @return {Array} Final array
 */
const appendIndexForEachElement = (array) => array.map((val, index) => ({ index, ...val }));

function generatePassword(settings) {
  if (settings) {
    const length = settings.min_pass_length.value;
    const string = 'ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz';
    const numeric = '0123456789';
    const punctuation = '!@#$%^&*()_+~`|}{:;?></.,=-';
    let password = '';
    let character = '';
    while (password.length < length) {
      const entity1 = Math.ceil(string.length * Math.random() * Math.random());
      const entity2 = Math.ceil(numeric.length * Math.random() * Math.random());
      const entity3 = Math.ceil(punctuation.length * Math.random() * Math.random());
      character += string.charAt(entity1);
      character += numeric.charAt(entity2);
      character += punctuation.charAt(entity3);
      password = character;
    }
    return password.toString().slice(-length);
  }
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz!$,._-';
  const stringLength = 8;
  let randomstring = '';
  for (let i = 0; i < stringLength; i += 1) {
    const rnum = Math.floor(Math.random() * chars.length);
    randomstring += chars.substring(rnum, rnum + 1);
  }
  return randomstring;
}

const validateEmail = (value) => /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);

const today = new Date();
today.setHours(0, 0, 0, 0);
const tomorrow = new Date(today);
tomorrow.setHours(23, 59, 59, 59);
const weekago = new Date(today);
weekago.setDate(today.getDate() - 7);

function dateToIsoFormat(date) {
  const isoDate = (new Date(date - (new Date()).getTimezoneOffset() * 60000)).toISOString();
  return isoDate.slice(0, 10);
}

function timeToIsoFormat(date) {
  const isoDate = (new Date(date - (new Date()).getTimezoneOffset() * 60000)).toISOString();
  return isoDate.slice(11, 16);
}

const requiredRule = (value) => !(value == null || isEmpty(String(value)))
  || i18n.t('%s cant be blank', [i18n.t('T_FIELD')]);

function getDirectoriesFromMarkers(acc, elem) {
  const elemInArrIndex = acc.findIndex((accElem) => accElem.id === elem.directory.id);
  if (elemInArrIndex !== -1) {
    acc[elemInArrIndex].markers.push(elem);
  } else {
    acc.push({
      id: elem.directory.id,
      name: elem.directory.name,
      markers: [elem],
    });
  }
  return acc;
}

function mobileCheck() {
  let check = false;
  const testMobile = (a) => {
    if (mobileCheckRegExp1.test(a)
    || mobileCheckRegExp2.test(a.substr(0, 4))) {
      check = true;
    }
  };
  testMobile(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

function isChrome() {
  const isChromium = window.chrome;
  const winNav = window.navigator;
  const vendorName = winNav.vendor;
  const isOpera = typeof window.opr !== 'undefined'
    || winNav.userAgent.indexOf('OP') > -1;
  const isIEedge = winNav.userAgent.indexOf('Edg') > -1;
  const isIOSChrome = winNav.userAgent.match('CriOS');
  return isIOSChrome
    || (isChromium !== null
    && typeof isChromium !== 'undefined'
    && vendorName === 'Google Inc.'
    && isOpera === false
    && isIEedge === false);
}

function parseAnswerTemplateForReplacement({
  template,
  customer,
  isExternalOperator,
  interpolationCodes,
}) {
  const replacing = (key) => {
    const dataDirection = interpolationCodes[key].replace(/_[^_.]+\./g, '.')
      .replace(/_\w/g, (item) => item.toUpperCase().replace('_', ''));
    const dirList = dataDirection.split('.');
    const dirFirst = dirList[0];
    const dirSecond = dirList[1];
    if (dirFirst === 'customers') {
      if (dirSecond === 'lastName' || dirSecond === 'middleName') {
        return isExternalOperator ? '' : customer[dirSecond] || '';
      }
      return customer[dirSecond];
    }
    // TODO: Сделать чтобы работало с другими типами данных не только customer
    return dataDirection;
  };

  let resultTemplate = template;

  Object.keys(interpolationCodes).forEach((key) => {
    if (resultTemplate.includes(key)) {
      const regex = new RegExp(key, 'g');
      resultTemplate = resultTemplate.replace(regex, [replacing(key)]);
    }
  });

  return resultTemplate.replace(/ +/g, ' ').replace(/\s(?=[!.?,):;}])/g, '').trim();
}

function addItemsType(array, type) {
  return array.map((x) => ({ ...x, type }));
}

export {
  isEmpty,
  isArray,
  l,
  isDevelop,
  dirname,
  formattedMessage,
  generateUniqueId,
  maskPhoneI8isValidNumber,
  maskPhoneI8Static,
  validateFormatPhone,
  appendIndexForEachElement,
  generatePassword,
  validateEmail,
  today,
  tomorrow,
  weekago,
  dateToIsoFormat,
  timeToIsoFormat,
  requiredRule,
  getDirectoriesFromMarkers,
  mobileCheck,
  isChrome,
  parseAnswerTemplateForReplacement,
  addItemsType,
};
